/**
 * workspaceFetch — singleton, deduplicating, cached fetcher for
 *                  /api/v2/workspaces/me (Feb 2026 stampede fix).
 *
 * Production stability triage (Feb 2026) found that 11+ components were
 * independently fetching this endpoint on mount, each with their own retry
 * loop, producing 7+ concurrent requests per page load. With Neon Postgres
 * cold-start (~3-5s) this saturated all uvicorn workers and cascaded 502s
 * onto unrelated MongoDB endpoints (auth/me, auth/login) — kicking users
 * to login.
 *
 * This module:
 *   - Deduplicates concurrent calls (one in-flight Promise shared by all callers)
 *   - Caches successful responses for 60s in module memory
 *   - Persists the cached payload in sessionStorage for cross-tab re-use
 *   - Centralises the 3-attempt retry with exponential backoff (1s, 3s, 9s)
 *   - Honors AbortController so callers can bail out
 *
 * Usage:
 *   import { fetchMyWorkspaces } from "@/lib/workspaceFetch";
 *   const list = await fetchMyWorkspaces();
 *   const wsId = list?.[0]?.workspace_id;
 */

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost dev. Without this guard, a stale
// REACT_APP_BACKEND_URL baked at deploy time can override the live host (e.g. askrex.info →
// pipeline-genius-5-r-….emergent.host) and every /api/v2/workspaces/me call returns 401 because
// the cookie doesn't cross domains. Production 12-May-2026 incident root cause.
const _API_BASE = (typeof window !== "undefined" && window.location.hostname !== "localhost")
  ? window.location.origin
  : (process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : ""));
const API = `${_API_BASE}/api`;
const ENDPOINT = "/v2/workspaces/me";
const STORAGE_KEY = "rex.workspaces_me.cache.v1";
const TTL_MS = 60_000;        // 60s cache
const MAX_ATTEMPTS = 3;
const TIMEOUT_MS = 10_000;    // 10s per attempt

let _inflight = null;          // Shared promise during a fetch
let _cache = null;             // { value, expires_at }

function _now() { return Date.now(); }

function _readSessionCache() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expires_at || parsed.expires_at < _now()) return null;
    return parsed;
  } catch (_e) { return null; }
}

function _writeSessionCache(value) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      value, expires_at: _now() + TTL_MS,
    }));
  } catch (_e) { /* quota/private mode — ignore */ }
}

function _wait(ms) { return new Promise(r => setTimeout(r, ms)); }

async function _doFetchOnce({ signal } = {}) {
  // 3 attempts with exponential backoff. Gives Neon up to ~13s of cold-start
  // headroom while still surfacing a hard error on real outage.
  let lastErr;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
    // Chain caller's signal so cancelling propagates
    if (signal) {
      if (signal.aborted) ctrl.abort();
      else signal.addEventListener("abort", () => ctrl.abort(), { once: true });
    }
    try {
      const r = await fetch(`${API}${ENDPOINT}`, {
        credentials: "include",
        signal: ctrl.signal,
      });
      clearTimeout(timer);
      if (r.status === 401 || r.status === 403) {
        // Auth failure — don't retry, surface immediately
        const err = new Error(`auth: ${r.status}`);
        err.status = r.status;
        throw err;
      }
      if (!r.ok) {
        lastErr = new Error(`HTTP ${r.status}`);
        lastErr.status = r.status;
        // 5xx: keep retrying
        if (attempt < MAX_ATTEMPTS) {
          await _wait(1000 * Math.pow(3, attempt - 1)); // 1s, 3s
          continue;
        }
        throw lastErr;
      }
      return await r.json();
    } catch (e) {
      clearTimeout(timer);
      if (signal && signal.aborted) throw e;
      // Treat a real auth failure as terminal
      if (e && (e.status === 401 || e.status === 403)) throw e;
      lastErr = e;
      if (attempt < MAX_ATTEMPTS) {
        await _wait(1000 * Math.pow(3, attempt - 1));
        continue;
      }
      throw lastErr;
    }
  }
  throw lastErr || new Error("workspace fetch failed");
}

export async function fetchMyWorkspaces({ force = false, signal } = {}) {
  // 1) Hot in-memory cache
  if (!force && _cache && _cache.expires_at > _now()) {
    return _cache.value;
  }
  // 2) Cross-tab session cache
  if (!force) {
    const sess = _readSessionCache();
    if (sess) {
      _cache = sess;
      return sess.value;
    }
  }
  // 3) Coalesce concurrent callers onto a single in-flight request
  if (_inflight) return _inflight;

  _inflight = (async () => {
    try {
      const value = await _doFetchOnce({ signal });
      _cache = { value, expires_at: _now() + TTL_MS };
      _writeSessionCache(value);
      return value;
    } finally {
      _inflight = null;
    }
  })();

  return _inflight;
}

export function clearWorkspaceCache() {
  _cache = null;
  _inflight = null;
  try { sessionStorage.removeItem(STORAGE_KEY); } catch (_e) {}
}

// Re-export under a friendly alias too — some callers want the first row.
export async function getMyDefaultWorkspaceId(opts) {
  const list = await fetchMyWorkspaces(opts);
  return Array.isArray(list) && list.length ? list[0].workspace_id : null;
}
